// @ts-nocheck
import { ThemeEvents, CartAddEvent } from "@theme/events";
import { morph } from "@theme/morph";

class CartRelatedProducts {
  /** @param {HTMLElement} root */
  constructor(root) {
    /** @type {HTMLElement} */
    this.root = root;
    /** @type {HTMLElement | null} */
    this.track = null;
    /** @type {HTMLButtonElement | null} */
    this.prevBtn = null;
    /** @type {HTMLButtonElement | null} */
    this.nextBtn = null;
    /** @type {Array<{productId:number,title:string,handle:string,image:string,variantId:number,available:boolean,price:number}>} */
    this.products = this.#readJson(".crp-products-json");
    /** @type {Set<number>} */
    this.cartVariantIds = new Set(
      /** @type {number[]} */ (this.#readJson(".crp-cart-ids-json"))
    );
    /** @type {number} */
    this.index = 0;

    // 状态：刷新去抖与并发保护
    /** @type {any} */
    this._refreshTimer = null;
    /** @type {boolean} */
    this._refreshing = false;
    /** @type {string} */
    this._productsSig = this.#productsSignature(this.products);
    /** @type {string} */
    this._cartSig = this.#cartSignature(this.cartVariantIds);

    // 添加调试信息
    console.log("CartRelatedProducts initialized with:", {
      productsCount: this.products.length,
      products: this.products,
      cartProductIds: Array.from(this.cartVariantIds),
    });

    this.#rebuild();
    this.#listenCartEvents();
  }

  #rebind() {
    // 先移除旧的事件监听器（如果存在的话）
    if (this.prevBtn && this.prevHandler) {
      this.prevBtn.removeEventListener("click", this.prevHandler);
    }
    if (this.nextBtn && this.nextHandler) {
      this.nextBtn.removeEventListener("click", this.nextHandler);
    }

    // 验证 root 元素存在且正确
    if (!this.root || !this.root.classList.contains("cart-related-products")) {
      return;
    }

    this.prevBtn = this.root.querySelector(".crp-prev");
    this.nextBtn = this.root.querySelector(".crp-next");

    // 创建新的事件处理函数
    this.prevHandler = () => this.#go(-1);
    this.nextHandler = () => this.#go(1);

    this.prevBtn?.addEventListener("click", this.prevHandler);
    this.nextBtn?.addEventListener("click", this.nextHandler);
  }
  #bind() {
    // 先移除旧的事件监听器（如果存在的话）
    if (this.track && this.trackHandler) {
      this.track.removeEventListener("click", this.trackHandler);
    }

    // 验证 root 元素存在且正确
    if (!this.root || !this.root.classList.contains("cart-related-products")) {
      return;
    }

    this.track = this.root.querySelector(".crp-track");

    // 创建新的事件处理函数
    this.trackHandler = (ev) => {
      /** @type {HTMLElement | null} */
      const target = ev.target instanceof HTMLElement ? ev.target : null;
      if (!target) return;
      const btn = target.closest(".crp-add");
      if (!(btn instanceof HTMLElement)) return;
      const itemEl = btn.closest(".crp-item");
      if (!(itemEl instanceof HTMLElement)) return;
      const productId = Number(itemEl.getAttribute("data-product-id"));
      const variantId = Number(itemEl.getAttribute("data-variant-id"));
      this.#addToCart({ productId, variantId, button: btn });
    };

    this.track?.addEventListener("click", this.trackHandler);
  }

  #listenCartEvents() {
    const schedule = () => this.#scheduleRefresh();
    document.addEventListener(ThemeEvents.cartUpdate, schedule);
    document.addEventListener(ThemeEvents.cartRemove, schedule);
  }

  #scheduleRefresh() {
    if (this._refreshTimer) clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => this.#refreshFromCart(), 120);
  }

  async #refreshFromCart() {
    if (this._refreshing) return;
    this._refreshing = true;
    try {
      // 获取最新的购物车数据（仅 JSON，不再请求整页 HTML）
      const cartRes = await fetch(`${Theme.routes.root}cart.js`, {
        headers: { Accept: "application/json" },
      });
      if (cartRes.ok) {
        const cart = await cartRes.json();
        const nextCartIds = new Set(
          /** @type {Array<any>} */ (cart.items || []).map((i) =>
            Number(i.variant_id)
          )
        );
        const nextCartSig = this.#cartSignature(nextCartIds);
        this.cartVariantIds = nextCartIds;
        this._cartSig = nextCartSig;
      }

      // 重新获取 root（如果页面的 section 被外部逻辑更新，确保拿到最新节点）
      const rootNow = document.getElementById("cart-related-products");
      if (!rootNow) {
        this.root = null;
        return;
      }
      this.root = rootNow;

      // 从当前 DOM 读取可能已更新的 JSON（避免整页请求）
      const parsed = /** @type {any[]} */ (
        this.#readJson(".crp-products-json")
      );
      const nextSig = this.#productsSignature(parsed);
      if (nextSig !== this._productsSig && parsed.length > 0) {
        this.products = parsed;
        this._productsSig = nextSig;
      }

      // 重置并刷新视图
      this.index = 0;
      this.#rebuild();
    } catch (e) {
      console.error("Error refreshing from cart:", e);
    } finally {
      this._refreshing = false;
    }
  }

  /** @template T @param {string} selector @returns {T[]} */
  #readJson(selector) {
    try {
      if (!this.root) return [];
      const el = this.root.querySelector(selector);
      if (!el) return [];
      return JSON.parse(el.textContent || "[]");
    } catch {
      return [];
    }
  }

  /** @param {Array<{productId:number,variantId:number}>} arr */
  #productsSignature(arr) {
    try {
      return (arr || [])
        .map((p) => `${Number(p.productId)}:${Number(p.variantId)}`)
        .join(",");
    } catch {
      return "";
    }
  }

  /** @param {Set<number>} set */
  #cartSignature(set) {
    try {
      return Array.from(set)
        .sort((a, b) => a - b)
        .join(",");
    } catch {
      return "";
    }
  }

  /** @returns {Array<{productId:number,title:string,handle:string,image:string,variantId:number,available:boolean,price:number}>} */
  #visibleProducts() {
    return this.products.filter(
      (p) => !this.cartVariantIds.has(Number(p.variantId))
    );
  }

  /** @param {number} step */
  #go(step) {
    const items = this.#visibleProducts();
    if (items.length === 0) return;

    const newIndex = this.index + step;
    this.index = Math.max(0, Math.min(items.length - 1, newIndex));

    this.#updateTransform(items.length);
    this.#updateArrows(items.length);
  }

  /** @param {number} count */
  #updateTransform(count) {
    // 验证 root 元素存在且正确
    if (!this.root || !this.root.classList.contains("cart-related-products")) {
      return;
    }

    // 重新获取 track 元素，因为 morph 可能改变了 DOM
    const track = this.root.querySelector(".crp-track");
    if (!(track instanceof HTMLElement)) {
      return;
    }

    const offset = -(this.index * 100);
    track.style.transform = `translateX(${offset}%)`;
  }

  /** @param {number} count */
  #updateArrows(count) {
    const disablePrev = this.index <= 0;
    const disableNext = this.index >= count - 1;
    this.prevBtn?.toggleAttribute("disabled", disablePrev);
    this.nextBtn?.toggleAttribute("disabled", disableNext);
  }

  #rebuild() {
    // 强制重新获取 root 元素
    this.root = document.getElementById("cart-related-products");

    // 如果 root 元素不存在，说明购物车可能为空，直接返回
    if (!this.root) {
      return;
    }

    // 验证 root 元素是否正确
    if (!this.root.classList.contains("cart-related-products")) {
      return;
    }

    this.#bind();
    this.#rebind();
    /** @type {HTMLElement | null} */
    const track = this.track;
    if (!(track instanceof HTMLElement)) {
      return;
    }
    const items = this.#visibleProducts();

    // 如果所有产品都已添加到购物车，隐藏整个组件
    if (items.length === 0) {
      this.root.style.display = "none";
      return;
    }

    // 创建新的 HTML 字符串
    const newHtml = items
      .map(
        (p) => `
      <li class="crp-item" data-product-id="${p.productId}" ${
          items.length == 1 ? "data-single='true'" : ""
        } data-variant-id="${p.variantId}">
        <div class="crp-item-left">
          <div class="crp-image">
            <img src="${p.image}" width="50" height="50" alt="${this.#escape(
          p.title
        )}">
          </div>
          <div class="crp-info">
            <div class="crp-name">${this.#escape(
              p.title
            )}<span class="crp-variant-title"> (${this.#escape(
          p.variantTitle
        )})</span></div>
            <div class="crp-price">${
              p.bundlePrice
                ? this.#formatMoney(p.bundlePrice)
                : this.#formatMoney(p.price)
            } ${
          p.bundlePrice
            ? `<s class="crp-old-price">${this.#formatMoney(p.price)}</s>`
            : ""
        }</div>
          </div>
        </div>
        <div>
          <button type="button" class="button crp-add" ${
            p.available ? "" : "disabled"
          }>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path path="" d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg></button>
        </div>
      </li>
    `
      )
      .join("");

    // 使用 morph 高效更新 DOM
    const newTrack = document.createElement("ul");
    newTrack.className = "crp-track";
    newTrack.style.cssText = track.style.cssText; // 保持现有样式
    newTrack.innerHTML = newHtml;

    morph(track, newTrack, { childrenOnly: true });

    // 重置 index 到第一个项目
    this.index = 0;

    this.#updateTransform(items.length);
    this.#updateArrows(items.length);
    this.root.classList.toggle("is-empty", items.length === 0);
  }

  /** @param {{productId:number, variantId:number, button: HTMLElement}} params */
  async #addToCart(params) {
    const { productId, variantId, button } = params;
    try {
      button.setAttribute("disabled", "true");
      button.classList.add("loading");
      const sectionIds = Array.from(
        document.querySelectorAll("cart-items-component")
      )
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter((v) => Boolean(v))
        .join(",");

      const form = new FormData();
      form.append("id", String(variantId));
      form.append("quantity", "1");
      if (sectionIds) form.append("sections", sectionIds);

      const fetchCfg = {
        method: "POST",
        body: form,
        headers: { Accept: "text/html" },
      };

      const res = await fetch(`${Theme.routes.root}cart/add.js`, fetchCfg);
      const data = await res.json();

      if (data.status) {
        document.dispatchEvent(
          new CartAddEvent({}, String(variantId), {
            didError: true,
            source: "cart-related-products",
            productId: String(productId),
            variantId: String(variantId),
          })
        );
        return;
      }

      // 立即添加到本地集合
      this.cartVariantIds.add(Number(variantId));
      this._cartSig = this.#cartSignature(this.cartVariantIds);

      // 立即重建视图
      this.#rebuild();

      // 延迟刷新购物车数据，确保API已更新
      setTimeout(() => {
        this.#refreshFromCart();
      }, 500);

      document.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: "cart-related-products",
          productId: String(productId),
          variantId: String(variantId),
          sections: data.sections,
        })
      );
    } catch (e) {
      document.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          didError: true,
          source: "cart-related-products",
          productId: String(productId),
          variantId: String(variantId),
        })
      );
    } finally {
      button.removeAttribute("disabled");
      button.classList.remove("loading");
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    try {
      const value = Number(cents) / 100;
      return `$${value.toFixed(2)}`;
    } catch {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  /** @param {string} s */
  #escape(s) {
    return String(s).replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[c])
    );
  }

  /** @param {string} s */
  #t(s) {
    if (s === "Add") return "Add";
    return s;
  }
}

function init() {
  const root = document.getElementById("cart-related-products");
  if (!root) {
    // 如果元素不存在，监听CartAddEvent事件
    const handleCartAdd = () => {
      const cartRelatedProductsElement = document.getElementById(
        "cart-related-products"
      );
      if (cartRelatedProductsElement) {
        document.removeEventListener(CartAddEvent.eventName, handleCartAdd);
        new CartRelatedProducts(cartRelatedProductsElement);
      }
    };

    document.addEventListener(CartAddEvent.eventName, handleCartAdd);
    return;
  }
  new CartRelatedProducts(root);
}

document.addEventListener("DOMContentLoaded", init);
